import { useEffect, useState } from "react";
import { COACH_BO_STYLES } from "./constants";
import { Card, Dropdown } from "react-bootstrap";
import { getDropdownCheck } from "./helpers";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { CompanySettings } from "./types";
import {
  selectCompanySettings,
  selectHasCoachBoConversationHistoryEnabled,
  toggleHasCoachBoConversationHistoryEnabled,
  updateCompanySettings,
} from "./slice";
import { useDebounceCallback } from "utils/hooks";
import Toggle from "app/components/Toggle";
import SimpleConfirmationModal from "app/components/Modals/SimpleConfirmationModal";
import {
  getCoachBoAvailableLanguages,
  selectAvailableCoachBoLanguages,
  updateCompanyCoachBoLanguage,
} from "../Chatbot/slice";
import SearchableDropdownAlt from "app/storybookComponents/SearchableInput/SearchableDropdownAlt";

const CoachBoAdminConsoleSettings = () => {
  const dispatch = useAppDispatch();

  // ------------------------ Selectors ------------------------------------
  const companySettings = useAppSelector(selectCompanySettings);
  const isConversationHistoryEnabled = useAppSelector(
    selectHasCoachBoConversationHistoryEnabled
  );
  const availableCoachBoLanguages = useAppSelector(
    selectAvailableCoachBoLanguages
  );

  // ------------------------ States ------------------------------------
  const [coachBoStyle, setCoachBoStyle] = useState<string>("normal");
  const [showConversationHistoryModal, setShowConversationHistoryModal] =
    useState(false);

  // ------------------------ Use Effects ------------------------------------
  useEffect(() => {
    setCoachBoStyle(companySettings?.coachBoStyle || "normal");
  }, [companySettings?.coachBoStyle]);

  useEffect(() => {
    dispatch(getCoachBoAvailableLanguages());
  }, [dispatch]);

  // ------------------------ Callbacks ------------------------------------
  const onSaveCoachBoConversationHistoryEnabled = useDebounceCallback(
    (value: boolean) => {
      const companyAccountId = companySettings?.companyAccountId;
      if (!companyAccountId) {
        return;
      }

      dispatch(
        updateCompanySettings({
          payload: {
            hasCoachBoConversationHistoryEnabled: Number(value) as 0 | 1,
          },
          companyAccountId: companyAccountId,
        })
      );
    },
    1000
  );

  const updateConversationHistory = () => {
    dispatch(
      toggleHasCoachBoConversationHistoryEnabled(!isConversationHistoryEnabled)
    );
    onSaveCoachBoConversationHistoryEnabled(!isConversationHistoryEnabled);
  };

  const handleConversationHistoryToggle = () => {
    if (!isConversationHistoryEnabled) {
      return updateConversationHistory();
    }

    setShowConversationHistoryModal(true);
  };

  const onSaveCompanySettings = useDebounceCallback(
    (value: CompanySettings["coachBoStyle"]) => {
      if (!companySettings?.companyAccountId) return;
      dispatch(
        updateCompanySettings({
          payload: { coachBoStyle: value },
          companyAccountId: companySettings.companyAccountId,
        })
      );
    },
    1000
  );

  const onCoachBoStyleChange = (value: string | null) => {
    if (!value) return;
    setCoachBoStyle(value);
    onSaveCompanySettings(value as "normal" | "conservative");
  };

  const getCoachBoLanguageSetting = () => {
    const selectedLanguage = companySettings?.coachBoLanguage ?? "English";
    const options = availableCoachBoLanguages.map((language) => ({
      value: language,
      label: language,
    }));

    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Coach Bo Language</h3>
          <p>
            Select the language Coach Bo will use when communicating with your
            employees.
          </p>
        </div>
        <div className="setting-row-action">
          <SearchableDropdownAlt
            options={options}
            onSelect={(language) =>
              dispatch(updateCompanyCoachBoLanguage(language))
            }
            selectedValue={selectedLanguage}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <SimpleConfirmationModal
        show={showConversationHistoryModal}
        hideModal={() => {
          setShowConversationHistoryModal(false);
        }}
        onActionClick={() => {
          updateConversationHistory();
          setShowConversationHistoryModal(false);
        }}
        title="Conversation History"
        contentText={
          <p
            style={{
              marginTop: "8px",
            }}
          >
            This will permanently delete all conversation history for everyone
            in your organization. <br />
            Note: Each individual can turn off conversation history in their
            user settings.
          </p>
        }
        contentTitle="Are you sure you want to turn off this setting for your entire organization?"
      />
      <Card className="admin-console-settings-card">
        <div>
          <h2 id="visibility-settings">Coach Bo</h2>
          <p>Settings related to Coach Bo chat.</p>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable Conversation History</h3>
            <p>
              All conversations with Coach Bo in your organization will be saved
              by default, but individual users can turn off their conversation
              history. Only individual users can see their conversation history.
            </p>
          </div>
          <div className="setting-row-action">
            <Toggle
              isOn={isConversationHistoryEnabled}
              handleToggle={handleConversationHistoryToggle}
            />
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Coach Bo Style</h3>
            <p>
              Specify the style of responses Coach Bo should reply with. In{" "}
              <u>normal</u> mode, Coach Bo will help users with a wide range of
              requests and enable more discussions around personal issues. In{" "}
              <u>conservative</u> mode, Coach Bo will redirect conversations to
              focus on work-related issues and refer users to organizational
              resources for topics outside of work.
            </p>
          </div>
          <div className="setting-row-action">
            <Dropdown onSelect={onCoachBoStyleChange}>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="row-gap-16px align-items-center"
              >
                {COACH_BO_STYLES[coachBoStyle]}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="normal">
                  {getDropdownCheck(coachBoStyle === "normal")}
                  {COACH_BO_STYLES.normal}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item eventKey="conservative">
                  {getDropdownCheck(coachBoStyle !== "open")}
                  {COACH_BO_STYLES.conservative}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        {getCoachBoLanguageSetting()}
      </Card>
    </>
  );
};

export default CoachBoAdminConsoleSettings;
